import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import PageHero from '../../components/PageHero';
import './NewsDetailsPage.css';
import newsHeroImg from '../../Assets/Image/News.webp';
import SEO from '../../components/SEO';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

function resolveImageUrl(url) {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_BASE_URL}${url}`;
}

const NewsDetailsPage = () => {
  const { id } = useParams();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/news/${id}`);
        setNews(res.data);
      } catch (err) {
        setNews(null);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, [id]);

  if (loading) {
    return (
      <div className="news-detail-page">
        <SEO title="Loading news…" noIndex />
        <PageHero title="Loading…" imagePath={newsHeroImg} />
        <section className="news-detail-content">
          <div className="container">
            <p>Loading article…</p>
          </div>
        </section>
      </div>
    );
  }

  if (!news) {
    return (
      <div className="news-detail-page">
        <SEO title="News not found" noIndex />
        <PageHero title="News Article Not Found" imagePath={newsHeroImg} />
        <section className="news-detail-content">
          <div className="container">
            <div className="news-not-found">
              <p>We could not find the news article you are looking for.</p>
              <Link to="/news" className="news-back-link">
                ← Back to News
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const heroImagePath = news.heroImage
    ? resolveImageUrl(news.heroImage)
    : newsHeroImg;

  return (
    <div className="news-detail-page">
      <SEO
        title={news.title}
        description={news.summary}
        image={news.heroImage ? resolveImageUrl(news.heroImage) : '/logo.webp'}
        canonicalPath={`/news/${news.id || id}`}
        type="article"
      />
      <PageHero title={news.title} imagePath={heroImagePath} />

      <motion.section
        className="news-detail-content"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.7, ease: 'easeOut' }}
      >
        <div className="container">
          <div className="news-detail-header">
            <p className="news-detail-date">{news.date}</p>
            <Link to="/news" className="news-back-link">
              ← Back to News
            </Link>
          </div>

          <div className="news-detail-layout">
            <article className="news-detail-article">
              {news.sections &&
                news.sections.map((section, index) => {
                  const sectionImageUrl = section.image
                    ? resolveImageUrl(section.image)
                    : null;
                  return (
                    <div key={index} className="news-section">
                      {section.heading && (
                        <h2 className="news-section-heading">{section.heading}</h2>
                      )}

                      {sectionImageUrl && (
                        <div className="news-section-image">
                          <img src={sectionImageUrl} alt={section.heading || news.title} />
                        </div>
                      )}

                      {section.paragraphs &&
                        section.paragraphs.map((paragraph, pIndex) => (
                          <p key={pIndex} className="news-section-paragraph">
                            {paragraph}
                          </p>
                        ))}
                    </div>
                  );
                })}
            </article>

            <aside className="news-detail-aside">
              <div className="news-highlight-box">
                <h3>Overview</h3>
                <p>{news.summary}</p>
              </div>
            </aside>
          </div>
        </div>
      </motion.section>
    </div>
  );
};

export default NewsDetailsPage;

